import {
  CUSTOM_FIELD_TYPES,
  useSaveCustomEmployeeFieldMutation,
  type CustomEmployeeField,
} from '@/app/features/control-panel/organization';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import { CirclePlusIcon, TrashIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form, Input, Select, Switch } from 'antd';
import { useEffect } from 'react';

type Props = CustomModalProps & {
  /** Absent when adding a new field. */
  field?: CustomEmployeeField;
};

const ManageCustomField = ({ open, handleClose, field }: Props) => {
  const [form] = Form.useForm();
  const [saveField, { isLoading }] = useSaveCustomEmployeeFieldMutation();

  const isEdit = Boolean(field?.id);
  // Only a select needs a list of choices.
  const fieldType = Form.useWatch('field_type', form);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      field_name: field?.field_name,
      field_type: field?.field_type || 'INPUT',
      options: field?.options?.length ? field.options : [''],
      is_required: field?.is_required ?? false,
      status: (field?.status ?? 'active') === 'active',
    });
  }, [open, field, form]);

  const handleSubmit = async (values: any) => {
    try {
      await saveField({
        id: field?.id,
        field_name: values.field_name?.trim(),
        field_type: values.field_type,
        // Choices only travel with a select, and blanks are dropped.
        options:
          values.field_type === 'SELECT'
            ? (values.options || [])
                .map((option: string) => option?.trim())
                .filter(Boolean)
            : undefined,
        is_required: Boolean(values.is_required),
        status: values.status ? 'active' : 'inactive',
      }).unwrap();

      toast('success', `Field ${isEdit ? 'updated' : 'created'} successfully`);
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to save the field'));
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit Custom Field' : 'Add Custom Field'}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        onFinish={handleSubmit}
        className="custom-form space-y-6"
      >
        <Form.Item
          label="Field Name"
          name="field_name"
          rules={[{ required: true, message: 'Please enter a label' }]}
        >
          <Input placeholder="Union ID" />
        </Form.Item>

        <Form.Item
          label="Field Type"
          name="field_type"
          rules={[{ required: true, message: 'Please select a type' }]}
        >
          <Select
            options={CUSTOM_FIELD_TYPES}
            disabled={isEdit}
            // Changing the type of a field that already holds values would
            // make those values unreadable, so it is fixed after creation.
            onChange={() => form.setFieldValue('options', [''])}
          />
        </Form.Item>

        {fieldType === 'SELECT' && (
          <Form.List name="options">
            {(fields, { add, remove }) => (
              <div className="space-y-3">
                <span className="text-sm text-white/45">Select Options</span>

                {fields.map(({ key, name }) => (
                  <Flex key={key} gap={12} align="start">
                    <Form.Item
                      name={name}
                      className="!mb-0 flex-1"
                      rules={[{ required: true, message: 'Enter an option' }]}
                    >
                      <Input placeholder={`Option ${name + 1}`} />
                    </Form.Item>
                    <Button
                      type="text"
                      disabled={fields.length === 1}
                      onClick={() => remove(name)}
                      icon={<TrashIcon size={18} />}
                      classNames={{ icon: 'flex items-center justify-center' }}
                      className="!h-11 !w-11 border border-theme-gray-800 rounded-md !text-theme-error-500"
                    />
                  </Flex>
                ))}

                <button
                  type="button"
                  onClick={() => add('')}
                  className="w-full h-11 rounded-md border border-dashed border-theme-primary/60 flex items-center justify-center gap-2 text-sm text-theme-primary hover:bg-theme-primary/10"
                >
                  <CirclePlusIcon size={18} />
                  Add option
                </button>
              </div>
            )}
          </Form.List>
        )}

        <Form.Item
          label="Required"
          name="is_required"
          layout="horizontal"
          className="border-b border-t !mb-0 !py-0.5 border-b-theme-gray-700 border-t-theme-gray-700 horizontal-end"
        >
          <Switch className="custom-switch" />
        </Form.Item>

        <Form.Item
          label="Status"
          name="status"
          layout="horizontal"
          className="border-b !mb-0 !py-0.5 border-b-theme-gray-700 horizontal-end"
        >
          <Switch className="custom-switch" />
        </Form.Item>

        <Flex justify="end">
          <Button
            htmlType="submit"
            loading={isLoading}
            className="btn-primary min-w-[80px] h-11 font-semibold"
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManageCustomField;
